import React, { useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import RightsBadge from './RightsBadge';

const { FiPlus, FiSearch } = FiIcons;

const uniq = list => [...new Set(list.filter(Boolean))].sort();

const Select = ({ value, onChange, label, options }) => (
  <select value={value} onChange={event => onChange(event.target.value)} className="text-xs border border-brand-dark rounded-lg px-2 py-1.5 bg-white">
    <option value="">{label}</option>
    {options.map(option => <option key={option} value={option}>{option}</option>)}
  </select>
);

const LibraryBrowser = ({ library = [], onAdd }) => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [mood, setMood] = useState('');
  const [instrument, setInstrument] = useState('');
  const [playlist, setPlaylist] = useState('');

  const options = useMemo(() => ({
    categories: uniq(library.map(piece => piece.category)),
    moods: uniq(library.map(piece => piece.mood)),
    instruments: uniq(library.flatMap(piece => piece.instruments || [])),
    playlists: uniq(library.flatMap(piece => piece.playlists || [])),
  }), [library]);

  const filtered = useMemo(() => library.filter(piece => {
    if (query && !`${piece.title} ${piece.tune} ${piece.composer}`.toLowerCase().includes(query.toLowerCase())) return false;
    if (category && piece.category !== category) return false;
    if (mood && piece.mood !== mood) return false;
    if (instrument && !(piece.instruments || []).includes(instrument)) return false;
    if (playlist && !(piece.playlists || []).includes(playlist)) return false;
    return true;
  }), [library, query, category, mood, instrument, playlist]);

  return (
    <div className="bg-white border border-brand-dark rounded-2xl p-4">
      <h3 className="font-serif text-lg font-bold text-accent-navy mb-3">Hymn library</h3>
      <div className="relative mb-2">
        <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
        <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search title, tune, composer"
          className="w-full pl-9 pr-3 py-2 text-sm border border-brand-dark rounded-lg" />
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        <Select value={category} onChange={setCategory} label="Category" options={options.categories} />
        <Select value={mood} onChange={setMood} label="Mood" options={options.moods} />
        <Select value={instrument} onChange={setInstrument} label="Instrument" options={options.instruments} />
        <Select value={playlist} onChange={setPlaylist} label="Playlist" options={options.playlists} />
      </div>
      {library.length === 0 && <p className="text-xs text-text-muted">The library is empty.</p>}
      <ul className="max-h-[60vh] overflow-y-auto divide-y divide-brand-dark/40">
        {filtered.slice(0, 100).map(piece => (
          <li key={piece.id} className="py-2 flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-accent-navy truncate">{piece.title}</p>
              <p className="text-[11px] text-text-muted truncate">{[piece.tune, piece.composer, piece.category].filter(Boolean).join(' · ')}</p>
              <div className="mt-1"><RightsBadge verdict={piece.lyricsVerdict} /></div>
            </div>
            <button type="button" onClick={() => onAdd('hymn', piece)} title="Add to programme"
              className="p-2 rounded-lg bg-accent-navy text-white">
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
      {filtered.length > 100 && <p className="text-[11px] text-text-muted mt-2">Showing first 100. Refine the filters to see more.</p>}
    </div>
  );
};

export default LibraryBrowser;